import type { ReactNode } from 'react'

/**
 * What an area shows when there is nothing in it.
 *
 * An empty view makes an offer: it says what will live here, and where there is a way to put the
 * first thing in, it holds that control. It never reports that there are no entries — that is a
 * fact about the database, not something anybody came here to read.
 */
export interface OfferProps {
  readonly title: string
  readonly lead: string
  /** A few concrete things that will live here. Concrete beats a summary. */
  readonly points?: readonly string[]
  /** The control that puts the first thing in, once there is one. */
  readonly action?: ReactNode
  /**
   * The plain truth when there is nothing to offer yet.
   *
   * While MyLib is being built, most areas are a frame around a space that is genuinely empty.
   * Saying so costs a line and is worth more than a screen that pretends to be a feature.
   */
  readonly note?: string
}

export function Offer({ title, lead, points, action, note }: OfferProps) {
  return (
    <div className="offer">
      <h2 className="offer__title">{title}</h2>
      <p className="offer__lead">{lead}</p>
      {points !== undefined && points.length > 0 && (
        <ul className="offer__points">
          {points.map((point) => (
            <li className="offer__point" key={point}>
              {point}
            </li>
          ))}
        </ul>
      )}
      {action !== undefined && <div className="controls">{action}</div>}
      {note !== undefined && <p className="offer__note">{note}</p>}
    </div>
  )
}
